/**
 * Machine room setup (spec §3.2) — THE BODY: room type, name, the car list, M4/M5,
 * and the group and hoistway builders every later section is generated from.
 *
 * The order is the specification's, and it is not cosmetic: M1 room type comes
 * first because it decides which question set the whole room runs (MRL runs the
 * Hydro set, tagged MRL — spec Q3), then the name, then the car list, then the
 * group and hoistway builders that decide how many Lobbies, Overhead and Pit
 * sections exist.
 *
 * ── Why this is a BODY and not a screen — Chip, 2026-08-18 ──────────────────
 * `room_setup` is a section in the walk: the hall indicator lists it and
 * Previous from Electrical lands on it. Until now those links opened the
 * generic section screen, which asked the catalog for the section's questions
 * and drew M1–M7 as ordinary rows — a hoistway multiselect with no options, a
 * car list with no cars, and §4 rule 7's *"Car 4 isn't in a hoistway yet."*
 * in red under an empty control. The screen that could actually place Car 4
 * was a different route, reachable only through the crumb and a ghost button:
 *
 *   *"There was no option to edit or correct the error, only the message
 *    appeared. … we could not return to the room-setup screen."*
 *
 * So the setup builder is now the section's body, rendered by `SectionScreen`
 * for every `isStructureSection` kind (declared beside the walk generator in
 * core/sections.ts) — with the section's own header, its wizard sentence, its
 * notes and photos, the D-021 reason at the moment of leaving, and Previous /
 * Next like every other stop. `RoomSetupScreen` (the `roomSetup` route the
 * building screen mints a room through) renders that same section screen the
 * moment the room exists; before it exists there is no section to render, so it
 * shows the type picker that creates one. One section, one screen, two URLs.
 *
 * ── What this body writes, and where ─────────────────────────────────────────
 * M1/M2/M3/M6/M7 are STRUCTURE (D-004): they go into `doc.rooms` through
 * `setStructure()`, never into the answer map. M4 and M5 are ordinary answers
 * and go through the shared `QuestionList` → `setAnswer()` like every other
 * question. (M4 is deliberately NOT auto-defaulted — `NO_AUTO_DEFAULT_QUESTION_IDS`
 * in the catalog says why: a silent No would delete the E4 shunt block and record
 * a fact about the room nobody checked. The shared renderer honours that list.)
 * Both paths commit to IndexedDB before React state moves.
 *
 * ── What this body does NOT decide ───────────────────────────────────────────
 *  - whether every car is placed — §4 rule 7, read off `validate()` and shown
 *    verbatim. The builder knows who owns a car (a fact in the tree); it does
 *    not know whether that is acceptable.
 *  - the unit count — `computeDerived()`'s M3 line, chip and all.
 *  - which M5 options exist — `visibleOptions(question, formSet)` (D-007), so a
 *    Traction room offers Overhead/Basement and a Hydro or MRL room offers
 *    Adjacent lowest / Adjacent top / Remote.
 *  - whether the room is finished — the gap engine, on the sections screen.
 */

import { useState, type ReactNode } from 'react';

import { Card, Eyebrow, QuestionList, Segmented, Stepper, TextField, WarnBox } from '../components';
import type { MachineRoom, RoomType, SectionInstance } from '../../core/types';
import { useSurvey } from '../state/SurveyProvider';
import { atsTaggingEnabled, ROOM_TYPE_LABEL } from '../state/buildingSetup';
import { addCarWithinBudget, carBudget } from '../state/carBudget';
import {
  MAX_LANDINGS,
  MIN_LANDINGS,
  addBucket,
  applyTypeChange,
  builderView,
  defaultRoomName,
  newCarId,
  newGroupId,
  newHoistwayId,
  nextCarLabel,
  removeBucket,
  removeCar,
  roomTypeOptions,
  setCarOnAts,
  setLandings,
  toggleCarInBucket,
  typeChangePlan,
  type BuilderView,
} from '../state/roomSetup';

export interface RoomSetupBodyProps {
  /** The `room_setup` section instance this body IS. Its scope names the room. */
  section: SectionInstance;
  /** The room, which must exist — the caller renders the type picker until it does. */
  machineRoom: MachineRoom;
}

export function RoomSetupBody({ section, machineRoom }: RoomSetupBodyProps): ReactNode {
  const { doc, results, setStructure, readOnly } = useSurvey();
  const roomId = machineRoom.id;

  /* Drafts and tab selection are the only local state here, and none of it is
   * an answer: the name draft lets a half-typed name survive a keystroke, and
   * the active tab is which bucket a tap lands in. */
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [pendingType, setPendingType] = useState<RoomType | null>(null);
  const [activeGroupId, setActiveGroupId] = useState<string | undefined>(undefined);
  const [activeHoistwayId, setActiveHoistwayId] = useState<string | undefined>(undefined);
  const typeOptions = roomTypeOptions();

  const withRoom = (mutate: (target: MachineRoom) => void): void => {
    void setStructure((document) => {
      const target = document.rooms.find((candidate) => candidate.id === roomId);
      if (target) mutate(target);
    });
  };

  /* ── M1 — changing the type of a room that exists ──────────────────────── */

  const chooseType = (next: RoomType): void => {
    if (next === machineRoom.type) return;
    // Nothing to lose — Hydro ⇄ MRL, or a room with no set-specific answers yet.
    if (typeChangePlan(doc, machineRoom, next).keys.length === 0) {
      void setStructure((document) => {
        applyTypeChange(document, roomId, next);
      });
      return;
    }
    setPendingType(next);
  };

  /*
   * D-030 R11 and the acceptance-4 rule, stated once: a car row carries an
   * ATS toggle exactly when the building says emergency power = YES (B14
   * visible through the visibility engine — one gate, one evaluator, D-003).
   * NO means no car ever shows one. Deterministic on the document alone.
   */
  const atsEnabled = atsTaggingEnabled(results);
  const groups = builderView(machineRoom, 'group', activeGroupId);
  const hoistways = builderView(machineRoom, 'hoistway', activeHoistwayId);
  const plan = pendingType ? typeChangePlan(doc, machineRoom, pendingType) : null;
  /* §4 rule 1 as a limit rather than a verdict — see state/carBudget.ts. */
  const budget = carBudget(doc, roomId);

  return (
    <>
      {/* ── M1 ───────────────────────────────────────────────────────────── */}
      <Eyebrow>Room type</Eyebrow>
      <Segmented
        options={typeOptions}
        value={machineRoom.type}
        ariaLabel="Room type"
        onChange={chooseType}
        disabled={readOnly}
      />

      {plan && pendingType ? (
        <WarnBox>
          <p>
            Switching to {ROOM_TYPE_LABEL[pendingType]} discards{' '}
            {plan.keys.length === 1 ? '1 answer' : `${String(plan.keys.length)} answers`} that only
            exist in the {ROOM_TYPE_LABEL[machineRoom.type]} question set:
          </p>
          <ul className="discardlist">
            {plan.questions.map((question) => (
              <li key={question.id}>{question.label}</li>
            ))}
          </ul>
          <div className="rowbtns">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setPendingType(null)}
            >
              Keep {ROOM_TYPE_LABEL[machineRoom.type]}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                // The plan is recomputed inside the transaction, against the
                // document as it is at write time — the list above is what the
                // surveyor was shown, not what authorises the delete.
                void setStructure((document) => {
                  applyTypeChange(document, roomId, pendingType);
                });
                setPendingType(null);
              }}
            >
              Change &amp; discard
            </button>
          </div>
        </WarnBox>
      ) : null}

      {/* ── M2 — one field; it labels itself (D-030) ─────────────────────── */}
      <Card>
        <TextField
          label="Machine room name"
          value={nameDraft ?? machineRoom.name}
          disabled={readOnly}
          onChange={(next) => {
            setNameDraft(next);
            // A room name is structure and the schema requires a non-empty one,
            // so an emptied box stores the positional default rather than an
            // invalid document the store would refuse on push.
            const name = next.trim() === '' ? defaultRoomName(doc, roomId) : next;
            withRoom((target) => {
              target.name = name;
            });
          }}
        />
      </Card>

      {/* ── M3 ───────────────────────────────────────────────────────────── */}
      <Eyebrow>Cars this room serves</Eyebrow>
      <Card>
        {machineRoom.cars.length === 0 ? null : (
          <ul className="carlist">
            {machineRoom.cars.map((car) => (
              <li key={car.id} className="carrow">
                <span className="carrow__label">{car.label}</span>
                {atsEnabled ? (
                  // D-047 (Chip, 2026-09-11): the "On ATS" pill read as a
                  // statement about the car, not a button — he thought a car
                  // was on the ATS because the words sat beside it. Stephanie
                  // chose a SWITCH from three one-line layouts: the knob says
                  // it moves, and off is visibly off. `onAts` is structure
                  // (CarRef), so `false` is the real opening state.
                  <div className="carrow__ats">
                    <span className="carrow__q" id={`ats-label-${car.id}`}>
                      On ATS
                    </span>
                    <button
                      type="button"
                      role="switch"
                      className="switch"
                      aria-checked={car.onAts}
                      aria-label={`${car.label} on ATS`}
                      disabled={readOnly}
                      onClick={() => {
                        withRoom((target) => {
                          setCarOnAts(target, car.id, !car.onAts);
                        });
                      }}
                    >
                      <span className="switch__track">
                        <span className="switch__knob" />
                      </span>
                    </button>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="rowbtn rowbtn--danger rowbtn--icon"
                  disabled={readOnly}
                  onClick={() => {
                    withRoom((target) => {
                      removeCar(target, car.id);
                    });
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 6h18" />
                    <path d="M8 6V4h8v2" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                  </svg>
                  <span className="sr-only">Remove {car.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/*
         * The building total is a LIMIT here, not a verdict at the end.
         *
         * From the design authority, on site: "I'm able to add seven more cars…
         * even though at the beginning I said there were only five elevators in
         * the building." §4 rule 1 knew, and only flagged — calmly, correctly,
         * and far too late to stop the twelfth car. So the budget is spent at
         * the tap.
         *
         * At the limit the control is HIDDEN, never greyed: a disabled button
         * is a puzzle a surveyor in a dim machine room has to solve, and D-015
         * asks for exactly one obvious next action. In its place stands the
         * sentence saying why, and the door out of it.
         */}
        {budget.canAdd ? (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={readOnly}
            onClick={() => {
              void setStructure((document) => {
                // Re-checked inside the transaction, against the document as it
                // is at write time — two taps in flight cannot both spend the
                // last car. Labels run building-wide, so room 2 starts at "Car 7".
                addCarWithinBudget(document, roomId, {
                  id: newCarId(),
                  label: nextCarLabel(document),
                  // D-047 postscript (Stephanie, 2026-09-11): a new car starts
                  // ON the ATS — on a building on emergency power most are, and
                  // the surveyor switches off the exception. Structure, not an
                  // answer, so D-016's "open on No" does not govern it; and it
                  // prices nothing unless B13 opens the ATS chain (B14/B17
                  // are gated on B13). Cars already on a record keep their flag.
                  onAts: true,
                });
              });
            }}
          >
            + Add car
          </button>
        ) : null}

        {/* No fix-the-total card and no unit-count chip (D-030): B11 is
          * edited on the Building screen, the reconciliation happens at close,
          * and the count is the car rows themselves. At the limit the + Add
          * car control is simply gone (D-015). */}
      </Card>

      {/* ── M4, M5 — ordinary answers, through the SHARED renderer ──────────
       *
       * `QuestionList` over this very section: `visibleQuestions` drops the
       * five structural ids (they are the tree above and below this block),
       * so what renders here is exactly M4 and M5 — with the per-question
       * chips, the note/photo toolbar, and the D-016 defaulting pass every
       * other section gets (which, for M4, deliberately writes nothing —
       * `NO_AUTO_DEFAULT_QUESTION_IDS`). One renderer, so these two rows
       * cannot drift from how the same questions look anywhere else. */}
      <QuestionList section={section} />

      {/* What the two builders below are FOR, said before they are used rather
       * than after both of them have been scrolled past. What they PRODUCED is
       * a different sentence and waits below until it is true. */}

      {/* ── M6 ───────────────────────────────────────────────────────────── */}
      <Eyebrow>Groups — banks with their own riser &amp; hall calls</Eyebrow>
      <Builder
        view={groups}
        noun="group"
        disabled={readOnly}
        onSelect={setActiveGroupId}
        onAdd={() => {
          const id = newGroupId();
          setActiveGroupId(id);
          withRoom((target) => {
            addBucket(target, 'group', id);
          });
        }}
        onRemoveTab={(id) => {
          setActiveGroupId(undefined);
          withRoom((target) => {
            removeBucket(target, 'group', id);
          });
        }}
        onToggleCar={(carId) => {
          const bucketId = groups.activeId;
          if (!bucketId) return;
          withRoom((target) => {
            toggleCarInBucket(target, 'group', bucketId, carId);
          });
        }}
        onLandings={(next) => {
          const bucketId = groups.activeId;
          if (!bucketId) return;
          withRoom((target) => {
            setLandings(target, bucketId, next);
          });
        }}
      />

      {/* ── M7 ───────────────────────────────────────────────────────────── */}
      <Eyebrow>Hoistways — which cars share a shaft &amp; pit</Eyebrow>
      <Builder
        view={hoistways}
        noun="hoistway"
        disabled={readOnly}
        onSelect={setActiveHoistwayId}
        onAdd={() => {
          const id = newHoistwayId();
          setActiveHoistwayId(id);
          withRoom((target) => {
            addBucket(target, 'hoistway', id);
          });
        }}
        onRemoveTab={(id) => {
          setActiveHoistwayId(undefined);
          withRoom((target) => {
            removeBucket(target, 'hoistway', id);
          });
        }}
        onToggleCar={(carId) => {
          const bucketId = hoistways.activeId;
          if (!bucketId) return;
          withRoom((target) => {
            toggleCarInBucket(target, 'hoistway', bucketId, carId);
          });
        }}
      />

    </>
  );
}

/* ═════════════════════════ the tap-to-assign builder ══════════════════════ */

interface BuilderProps {
  view: BuilderView;
  noun: 'group' | 'hoistway';
  onSelect: (id: string) => void;
  onAdd: () => void;
  onRemoveTab: (id: string) => void;
  onToggleCar: (carId: string) => void;
  /** Groups only — hoistways have no landings. */
  onLandings?: (next: number) => void;
  disabled?: boolean;
}

/**
 * The mockup's group / hoistway builder, both dimensions from one component.
 *
 * The gesture: pick a tab, then tap cars into it. A car owned by ANOTHER bucket
 * renders dimmed and dashed and is not tappable — moving a car is take-it-out-
 * then-put-it-in, which makes the double membership §4 rule 7 rejects
 * unreachable through the UI rather than merely invalid once made.
 *
 * The status line underneath is `builderStatus`'s, and the sentences naming the
 * cars still to place are the validation engine's, verbatim. What this component
 * decides is nothing at all.
 */
function Builder({
  view,
  noun,
  onSelect,
  onAdd,
  onRemoveTab,
  onToggleCar,
  onLandings,
  disabled = false,
}: BuilderProps): ReactNode {
  const plural = `${noun}s`;
  const activeTab = view.tabs.find((tab) => tab.active);

  return (
    <Card ariaLabel={`${noun} builder`}>
      {/* `role="group"` + `aria-pressed`, not a tablist: there are no tab panels
       * here — the car pool below is one shared surface whose meaning changes
       * with the selection, and announcing it as a tabbed interface would
       * promise a structure that does not exist. */}
      <div className="gb-tabs" role="group" aria-label={`${plural} in this room`}>
        {view.tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            aria-pressed={tab.active}
            className={tab.active ? 'gb-tab gb-tab--on' : 'gb-tab'}
            disabled={disabled}
            onClick={() => onSelect(tab.id)}
          >
            {tab.name}
            <small data-legibility="display">
              {tab.carCount === 1 ? '1 car' : `${String(tab.carCount)} cars`}
            </small>
          </button>
        ))}
        <button type="button" className="gb-tab gb-tab--new" disabled={disabled} onClick={onAdd}>
          + New
        </button>
      </div>


      {view.cars.length > 0 && view.activeId ? (
        <div className="carpool" role="group" aria-label={`Cars — tap to assign to ${view.activeName ?? ''}`}>
          {view.cars.map((car) => {
            const className =
              car.state === 'mine'
                ? 'carbtn carbtn--mine'
                : car.state === 'other'
                  ? 'carbtn carbtn--other'
                  : 'carbtn';
            return (
              <button
                key={car.carId}
                type="button"
                className={className}
                aria-pressed={car.state === 'mine'}
                // Owned elsewhere: not a target. The mockup dims and dashes it;
                // `disabled` is what makes that true for a thumb and for a
                // screen reader, not just for the eye. Round 7 task 10: a
                // read-only survey disables every car in the pool, owned or not.
                disabled={disabled || car.state === 'other'}
                aria-label={
                  car.state === 'other'
                    ? `${car.label}, already in ${car.ownerName ?? 'another ' + noun}`
                    : car.label
                }
                onClick={() => onToggleCar(car.carId)}
              >
                {car.label}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Prompt, then explainer, then the −/+ — `.gb-landings` stacks so the
       * sentence that says what a landing IS is read before it is counted. */}
      {onLandings && view.landings !== undefined && view.activeId ? (
        <div className="gb-landings">
          <span className="stepper-row__label">
            Landings {view.activeName} serves
          </span>
          {/* Keyed by group (D-053): switching groups is a new box, so a number
           * typed for one and not yet blurred is written to THAT group on the
           * way out, never carried over to the next. */}
          <Stepper
            key={view.activeId}
            value={view.landings}
            min={MIN_LANDINGS}
            max={MAX_LANDINGS}
            ariaLabel={`Landings ${view.activeName ?? ''} serves`}
            onChange={onLandings}
            disabled={disabled}
          />
        </div>
      ) : null}

      {activeTab?.removable ? (
        <button type="button" className="rowbtn" disabled={disabled} onClick={() => onRemoveTab(activeTab.id)}>
          Remove {activeTab.name}
        </button>
      ) : null}

    </Card>
  );
}
